/**
 * 아파트 카드(AptSummaryCard / AptChartCard)에 필요한 풍부 정보를 fetch하여 enrich한다.
 * favorite-apt-summary.js와 favorite-apt-dialog.js에서 공통 사용.
 *
 * - 실거래 (recentDeal/prevDeal/realDealChange, recentLease/prevLease/realLeaseChange)
 * - 신고가 (isNewHigh)
 * - 매매/전세 시세 변동 (dealStat/leaseStat, dealPriceChange, dealPriceChangeRate)
 */
import { aptApi } from "src/api/apt";
import { dashboardApi } from "src/api/dashboard";

/** 실거래 price 문자열을 숫자(만원)로 파싱 */
const parsePrice = (priceStr) => {
  if (priceStr == null) return null;
  if (typeof priceStr === "number") return priceStr;
  const num = parseInt(String(priceStr).replace(/,/g, ""), 10);
  return isNaN(num) ? null : num;
};

/** aptName + pyeong 매칭으로 가장 최근 매매/전세 실거래 추출 */
const extractRealPrices = (realPrices, apts) => {
  const rpArr = Array.isArray(realPrices) ? realPrices : [];
  const map = {};

  apts.forEach((apt) => {
    const key = `${apt.aptName}__${apt.supplyPyeong}`;
    const aptNameTrimmed = (apt.aptName || "").trim();
    const list = rpArr.filter((rp) =>
      (rp.aptName || "").trim() === aptNameTrimmed && rp.pyeong === apt.supplyPyeong,
    );

    const extractRecent = (type) => {
      const items = list
        .filter((rp) => rp.type === type && !rp.isDeleted)
        .sort((a, b) => (b.date || 0) - (a.date || 0));
      const recent = items[0] || null;
      const prev = items[1] || null;
      if (!recent) return { recent: null, prev: null, change: null };
      const recentPrice = parsePrice(recent.price);
      const prevPrice = prev ? parsePrice(prev.price) : null;
      return {
        recent: { ...recent, priceNum: recentPrice },
        prev: prev ? { ...prev, priceNum: prevPrice } : null,
        change: recentPrice != null && prevPrice != null ? recentPrice - prevPrice : null,
      };
    };

    const deal = extractRecent("매매");
    const lease = extractRecent("전세");

    map[key] = {
      recentDeal: deal.recent,
      prevDeal: deal.prev,
      realDealChange: deal.change,
      recentLease: lease.recent,
      prevLease: lease.prev,
      realLeaseChange: lease.change,
    };
  });

  return map;
};

/**
 * 아파트 배열에 실거래 + 신고가 + 시세변동 데이터를 enrich한다.
 * @param {Array} apts - SPyeongPriceDto 배열
 * @returns {Promise<Array>} enrich된 아파트 배열
 */
export const enrichApts = async (apts) => {
  if (!apts || apts.length === 0) return [];
  const pyeongIds = apts.map((a) => a.pyeongId).filter(Boolean);
  const regionIds = pyeongIds.map(String).join(",");

  // 동일 평형 ID 범위의 세 지표를 한 API로 묶어 전국 topN 쿼리와 중복 요청을 피한다.
  const [realPrices, statsRes] = await Promise.all([
    aptApi.getSPyeongsRealPrices({ ids: pyeongIds, limit: 500 }).catch(() => []),
    dashboardApi.getStats({
      metrics: "NH_DEAL,MP_DEAL,MP_LEASE,SC_DEAL,SC_LEASE",
      regionType: "SPYEONG",
      regionIds,
    }).catch(() => ({})),
  ]);

  const nhSet = new Set();
  (statsRes["NH_DEAL"] || []).forEach((item) => { if (item.regionId) nhSet.add(item.regionId); });
  const dealStats = new Map(
    (statsRes["MP_DEAL"] || []).map((item) => [String(item.regionId), item]),
  );
  const leaseStats = new Map(
    (statsRes["MP_LEASE"] || []).map((item) => [String(item.regionId), item]),
  );
  const dealCountStats = new Map(
    (statsRes["SC_DEAL"] || []).map((item) => [String(item.regionId), item]),
  );
  const leaseCountStats = new Map(
    (statsRes["SC_LEASE"] || []).map((item) => [String(item.regionId), item]),
  );

  const rpMap = extractRealPrices(realPrices, apts);

  return apts.map((a) => {
    const pid = String(a.pyeongId);
    const rpKey = `${a.aptName}__${a.supplyPyeong}`;
    const rp = rpMap[rpKey] || {};
    const deal = dealStats.get(pid) ?? null;
    const lease = leaseStats.get(pid) ?? null;
    const pc = {
      dealStat: deal,
      dealPriceChange: deal?.changeValue ?? null,
      dealPriceChangeRate: deal?.changeRate ?? null,
      leaseStat: lease,
      leasePriceChange: lease?.changeValue ?? null,
      leasePriceChangeRate: lease?.changeRate ?? null,
      dealCountStat: dealCountStats.get(pid) ?? null,
      leaseCountStat: leaseCountStats.get(pid) ?? null,
    };
    return { ...a, isNewHigh: nhSet.has(pid), ...rp, ...pc };
  });
};
